<script lang="ts">
  import { browser } from "$app/environment";
  import { scale } from "svelte/transition";
  import { renderRichText } from "@storyblok/svelte";
  import { userDetails } from "$lib/consumer/stores/userDetails";

  // Get the block data
  export let blok;

  // Get the rendered privacy policy message
  $: renderedPrivacyPolicy = blok?.privacyPolicyMessage
    ? renderRichText(blok.privacyPolicyMessage)
    : "";

  // Get the user details
  $: ({ street, city, state, zipCode, country, addressString } = $userDetails);

  // Initialize the form state
  let email = "";
  let submitting = false;
  let success = false;
  let error = false;

  /**
   * Handles the form submission
   * Sends the request to the api/braze/users-track proxy endpoint
   * @param event
   */
  async function handleSubmit(event: Event) {
    event.preventDefault();

    // Confirm an email address is provided, and that the form is not already submitting
    if (!email || submitting) return;

    // Set the submitting state
    submitting = true;

    // Send the request
    try {
      const response = await fetch("/api/braze/users-track", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          email,
          street,
          city,
          state,
          zipCode,
          country,
          addressString,
        }),
      });

      if (response.ok) {
        // Show the success message
        success = true;

        // Trigger the GTM submit event
        if (browser && window.dataLayer) {
          window.dataLayer.push({
            event: "waitlist_email_form_submit",
          } as any);
        }
      } else {
        // Handle the error
        throw new Error("Form submission failed");
      }
    } catch (err) {
      // Show the error message
      error = true;
      console.error(err);
    } finally {
      // Reset the form submission state
      submitting = false;
    }
  }

  /**
   * Function to hide previous error messages when the input field is focused
   */
  function handleInputFocus() {
    if (error) {
      error = false;
    }
  }
</script>

<section class="email-form-section">
  <div class="email-form-wrapper">
    {#if !success}
      <form class="email-form" on:submit={handleSubmit}>
        <input
          bind:value={email}
          on:focus={handleInputFocus}
          type="email"
          class="email-form-input"
          placeholder="Enter your email"
          required
        />

        <button type="submit" class="email-form-submit-button" disabled={submitting}>
          <span class="email-form-submit-button-arrow">→</span>
        </button>

        <input type="hidden" name="street" value={street} />
        <input type="hidden" name="city" value={city} />
        <input type="hidden" name="state" value={state} />
        <input type="hidden" name="zipCode" value={zipCode} />
        <input type="hidden" name="country" value={country} />
        <input type="hidden" name="addressString" value={addressString} />
      </form>
    {/if}

    {#if success}
      <div class="success-message" in:scale={{ duration: 300, start: 0.95 }}>
        <h2>{blok.formSuccessMessage}</h2>
      </div>
    {/if}

    {#if error}
      <div class="error-message">{blok.formErrorMessage}</div>
    {/if}
  </div>

  <div class="privacy-policy-message">{@html renderedPrivacyPolicy}</div>
</section>

<style lang="postcss">
  .email-form-section {
    display: flex;
    flex-direction: column;
    align-items: center;
    width: 100%;
  }

  .email-form-wrapper {
    position: relative;
    display: flex;
    flex-direction: column;
    align-items: center;
    margin-top: 2rem;

    @screen md {
      margin-top: 3rem;
    }
  }

  .email-form {
    display: flex;
    border: 1px solid #fff;
    border-radius: 2px;
    overflow: hidden;
  }

  .email-form-input {
    flex: 1;
    display: flex;
    align-items: center;
    width: 260px;
    height: 65px;
    padding: 0 20px;
    font-family: Plain;
    font-size: 16px;
    font-weight: 400;
    line-height: 1;
    background-color: transparent;
    border: none;
    border-radius: 0;
    color: #fff;

    @screen md {
      width: 340px;
    }

    &:focus {
      outline: none;
    }

    &::placeholder {
      color: #fff;
    }
  }

  .email-form-submit-button {
    width: 65px;
    height: 65px;
    font-family: Plain;
    font-size: 22px;
    font-weight: 200;
    line-height: 1;
    color: #000;
    background-color: #fff;
    border: none;
    border-radius: 0;
    border-left: 1px solid #fff;
    cursor: pointer;
    background-color: rgb(var(--color-consumer-yellow));
    transition: all 0.2s ease;

    .email-form-submit-button-arrow {
      display: inline-block;
      transition: transform 0.2s ease;
    }

    &:hover,
    &:focus {
      filter: brightness(108%);

      .email-form-submit-button-arrow {
        transform: scale(1.2);
      }
    }

    &:focus {
      outline: none;
    }
  }

  .privacy-policy-message {
    margin-top: 1rem;
    font-family: Plain;
    font-size: 12px;
    font-weight: 400;
    line-height: 1.5;
    color: rgba(255, 255, 255, 0.6);
    text-align: center;
  }

  :global(.privacy-policy-message a) {
    color: rgba(255, 255, 255, 0.6);
    text-decoration: underline;
  }

  .success-message {
    padding: 2rem;
    background-color: rgba(255, 255, 255, 0.2);
    border-radius: 4px;
  }

  .success-message h2 {
    font-family: var(--font-secondary);
    font-size: 30px;
    font-weight: 600;
    line-height: 1.1;
    color: #fff;
    text-align: center;

    @screen md {
      font-size: 54px;
    }
  }

  .error-message {
    position: absolute;
    width: 100%;
    padding: 20px;
    top: 100%;
    left: 0;
    border-bottom-left-radius: 2px;
    border-bottom-right-radius: 2px;
    border-top: 0;
    color: #fff !important;
    background: rgba(0, 0, 0, 0.8);
    font-size: 14px;
    text-align: center;
    z-index: 10;
  }
</style>
